import { useAgents } from '#~/agent/hooks.js';
import { fieldKeyedAgentsEqual, watchdogAgentPick } from './use-agents-field-eq.js';
import { broker, type Proxy } from '#~/agent/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { createBridgeLifecycleTracker } from '#~/analytics/domains/index.js';
import { logBridgeConnState, type BridgeConnState } from '#~/logger/observability.js';
import { ElectronAPI } from '#~/electron/index.js';
import {
  DESKTOP_UPDATE_SNOOZE_MS,
  getDesktopUpdateSnoozeState,
  setDesktopUpdateSnooze,
  subscribeDesktopUpdateSnooze,
  type DesktopUpdateSnoozeState
} from '#~/electron/update-snooze.js';
import { isElectronApp } from '#~/electron/utils.js';
import { logger } from '#~/logger/index.js';
import { clear, has, hide, post } from '#~/notifications/index.js';
// Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — the update
// toast is an e2e target, so its body and actions carry STags instead of being
// matched on copy. Sel.DesktopUpdateNag lives in src/stag/tag.ts and is
// mirrored in studio/electron/e2e/helpers/selectors.ts.
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { usePopupStore } from '#~/store/popup.js';
import { update } from '../commands/update.js';
import { MessageId } from './constants.js';
import { useBridgeConnectionNotifications } from './bridge-connection-notifications.js';
import { useLowDiskNotifications } from './low-disk-notifications.js';
import { shouldPostUpdateNag, useProxyUpdateStatus } from './update-nag.js';
import { useBridgeVersionNotice } from './bridge-version-notice.js';
import { Button } from '@studio/design-system/button';
import { usePrompt } from '@studio/design-system/prompt';
import { getVendorContent, vendorName } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import { useUnmount } from '@studio/react-utils/hooks';
import { StatusUpdateState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { pick } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { ReactElement } from 'react';

/** Grace period in milliseconds before firing bridge offline event during startup */
const STARTUP_GRACE_PERIOD_MS = 10_000;

const RELEASE_NOTES_URL = getVendorContent({
  iris: 'https://intercom.help/iris-ai/en/articles/11750950-release-notes',
  ptzoptics: 'https://hivehelp.ptzoptics.com/en/articles/9067550-release-notes'
});

/** Agent `<WatchdogPiece>` component. */
const AgentWatchdog = ({}: { agent: Pick<Agent, 'embedded' | 'id' | 'name' | 'status'> }): null => {
  useUnmount(() => {
    hide(MessageId);
  });

  return null;
};

// Module-level so leaving and re-entering the room does not re-post the same version.
let lastPostedUpdateVersion: string | null = null;

const UNKNOWN_SNOOZE_SNAPSHOT: DesktopUpdateSnoozeState = { known: false, snooze: null };

export const ProxyWatchdog = ({ proxy }: { proxy: Proxy }) => {
  useLowDiskNotifications(proxy);
  const { state, latestVersion } = useProxyUpdateStatus(proxy.id);

  const snoozeState = useSyncExternalStore(
    subscribeDesktopUpdateSnooze,
    getDesktopUpdateSnoozeState,
    () => UNKNOWN_SNOOZE_SNAPSHOT
  );
  const { setPopup } = usePopupStore();

  const prompt = usePrompt();
  const { track } = useAnalytics();
  const bridgeLifecycleTracker = useMemo(() => createBridgeLifecycleTracker(track), [track]);

  const { id, embedded, name } = proxy;

  const status = useBridgeConnectionNotifications({
    id,
    name: name ?? `${vendorName()} Bridge`,
    embedded
  });

  // Track startup time and connection state for bridge offline event delay logic
  const startupTimeRef = useRef<number>(Date.now());
  const hasEverConnectedRef = useRef<boolean>(false);
  const offlineEventTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // W12 — track the previous status so the observability emitter can
  // log a `from -> to` transition. We use a ref (not state) because the
  // value should NOT trigger re-render and the existing status effect
  // already runs on every change. Initial value 'unknown' per
  // logger/observability.ts contract for the first transition.
  const prevStatusRef = useRef<BridgeConnState | 'unknown'>('unknown');

  // The shared domain tracker keeps this watchdog on the canonical-only sender.
  // Historical PostHog readers still cover `bridge_goes_offline` rows.
  const fireBridgeOfflineEvent = useCallback(() => {
    bridgeLifecycleTracker.trackBridgeConnectionUnavailable({
      studioId: proxy.roomId,
      agentId: id,
      agentName: name!,
      vendorName: vendorName()
    });
  }, [bridgeLifecycleTracker, proxy.roomId, id, name]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (offlineEventTimeoutRef.current) {
        clearTimeout(offlineEventTimeoutRef.current);
        offlineEventTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // W12 — log the shared connection-state transition. We log every transition (online,
    // offline, reconnecting, idle, booting), which matches the W12
    // acceptance ("Bridge connection-state transitions logged on every
    // transition"). De-dupe: only emit when status actually changed,
    // since this useEffect fires whenever any of the deps change.
    const prevStatus = prevStatusRef.current;
    if (status !== undefined && prevStatus !== status) {
      // Cast: studio.client's Status type has more variants than the
      // observability emitter's BridgeConnState union, but the emitter
      // accepts any string at the type boundary so this is safe — the
      // canonical line still appears. We accept the breadth to keep the
      // emitter contract tight on the four states support actually
      // greps for.
      logBridgeConnState(id, prevStatus, status as BridgeConnState);
      prevStatusRef.current = status as BridgeConnState;
    }

    if (embedded) {
      return;
    }

    if (status === 'online') {
      // Mark that the bridge has connected at least once
      hasEverConnectedRef.current = true;

      // Cancel any pending offline event since we're now online
      if (offlineEventTimeoutRef.current) {
        clearTimeout(offlineEventTimeoutRef.current);
        offlineEventTimeoutRef.current = null;
      }
    } else if (status === 'offline') {
      const timeSinceStartup = Date.now() - startupTimeRef.current;

      // Bridge offline ANALYTICS event delay/cancellation logic (unchanged):
      // - If bridge has already connected before: fire immediately (genuine disconnect)
      // - If bridge has never connected AND within startup grace period: delay the event
      // - If bridge has never connected AND past startup grace period: fire immediately
      const isWithinGracePeriod = timeSinceStartup < STARTUP_GRACE_PERIOD_MS;

      if (hasEverConnectedRef.current) {
        // Bridge was connected before, this is a genuine disconnect - fire immediately
        fireBridgeOfflineEvent();
      } else if (isWithinGracePeriod) {
        // Within startup grace period and never connected - delay the event
        // Cancel any existing timeout first
        if (offlineEventTimeoutRef.current) {
          clearTimeout(offlineEventTimeoutRef.current);
        }

        const remainingGracePeriod = STARTUP_GRACE_PERIOD_MS - timeSinceStartup;
        offlineEventTimeoutRef.current = setTimeout(() => {
          // Only fire if still not connected after grace period
          if (!hasEverConnectedRef.current) {
            fireBridgeOfflineEvent();
          }
          offlineEventTimeoutRef.current = null;
        }, remainingGracePeriod);
      } else {
        // Past startup grace period and never connected - fire immediately
        fireBridgeOfflineEvent();
      }
    }
    // eslint-disable-next-line react-hooks-configurable/exhaustive-deps
  }, [status, id, name, embedded, fireBridgeOfflineEvent]);

  const installNow = async () => {
    prompt.confirm({
      ...DataSTag(Sel.DesktopUpdateNag, 'prompt'),
      title: `Update Bridge?`,
      message: `Are you sure you want to update the ${vendorName()} Bridge software?  This will disconnect all video and audio sources until the agent is updated and restarted.`,
      okay: {
        label: 'Update Bridge',
        variant: 'danger'
      },
      onConfirm: async (answer: boolean) => {
        if (!answer) {
          return;
        }

        clear('update-available');

        // Open the bridge modal
        setPopup('bridge');
        bridgeLifecycleTracker.trackBridgeTrayOpened({ surface: 'watchdog_update_prompt' });

        // Send the update command
        proxy.mutate(
          {
            status: {
              update: {
                state: StatusUpdateState.UPDATE_STATE_INSTALLING
              }
            }
          },
          {}
        );

        await update(proxy.id);
      }
    });
  };

  useBridgeVersionNotice(proxy, installNow);

  useEffect(() => {
    if (
      !shouldPostUpdateNag({
        state,
        latestVersion,
        snooze: snoozeState.snooze,
        snoozeKnown: snoozeState.known,
        now: Date.now(),
        lastPostedVersion: lastPostedUpdateVersion,
        alreadyPosted: has('update-available')
      })
    ) {
      return;
    }

    lastPostedUpdateVersion = latestVersion!;

    let dismissPrompt = () => {
      const version = latestVersion!;
      // Local first so the effect cannot re-post between clear() and main's status echo.
      setDesktopUpdateSnooze({ version, until: Date.now() + DESKTOP_UPDATE_SNOOZE_MS });
      if (isElectronApp()) {
        ElectronAPI.snoozeUpdate(version)?.catch((error) =>
          logger.error('update:snooze failed', error)
        );
      }
      clear('update-available');
    };

    const showReleaseNotes = () => {
      window.open(RELEASE_NOTES_URL, '_blank');
    };

    const title = `Version ${latestVersion} is now available`;
    const message = (
      // The offered version is the uniqueID so a test can assert on "the toast
      // for THIS version" — the notification id is the constant
      // 'update-available', which cannot tell two versions apart.
      <div {...DataSTag(Sel.DesktopUpdateNag, 'toast', latestVersion!)}>
        <span className='text-[12px]'>
          A new version of {vendorName()} is available! Install now to get the latest features and
          bug fixes.
        </span>
        <Button
          variant='link'
          className='px-0 pt-12 text-secondary underline'
          onClick={showReleaseNotes}
          {...STagNew(Sel.DesktopUpdateNag, 'release-notes')}
        >
          Release notes
        </Button>
        <div className='mb-0 flex flex-row gap-8'>
          <Button
            variant='link'
            className='px-0 pt-12 text-secondary no-underline'
            onClick={dismissPrompt}
            {...STagNew(Sel.DesktopUpdateNag, 'later')}
          >
            Later
          </Button>
          <Button
            variant='link'
            className='px-0 pt-12 text-primary no-underline'
            onClick={installNow}
            {...STagNew(Sel.DesktopUpdateNag, 'install-now')}
          >
            Install Now
          </Button>
        </div>
      </div>
    );

    post(
      {
        title,
        message,
        id: 'update-available'
      },
      { autoClose: false }
    );
  }, [state, latestVersion, snoozeState]);

  return <div></div>;
};

/** `<Watchdog>` component. */
export const Watchdog = (): ReactElement => {
  const agents = useAgents(
    (µ) => µ.map((a) => pick(a, ['embedded', 'id', 'name', 'status'])),
    (a, b) => fieldKeyedAgentsEqual(a, b, watchdogAgentPick)
  );

  const proxies = agents.map((a) => broker.acquire(a.id));

  return (
    <>
      {agents.map((agent) => (
        <AgentWatchdog agent={agent} key={agent.id} />
      ))}

      {proxies
        .filter((proxy) => !!proxy)
        .map((proxy) => (
          <ProxyWatchdog key={proxy?.id} proxy={proxy!} />
        ))}
    </>
  );
};
